import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { INVOICE_LIST_STATE, INVOICE_STATE, INVOICE_TYPE, SORT } from "./InvoiceEnums";
import { IInvoiceFilter, ISearchTag } from "./InvoiceTypes";
import { GetRealestateUserAndAccessData_realestateUsersAndAccess } from "src/api/generated/GetRealestateUserAndAccessData";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { formatDate } from "./Utils";
import { format } from "date-fns";
import i18next from "i18next";
import { persist } from "mobx-persist";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import { Route, selectRoute } from "src/config/routes";
import Axios from "src/network/Axios";

type BatchProcessMap = {
    [invoiceNumber: number]: boolean;
};

type ListStateSelectedMap = {
    [K in INVOICE_LIST_STATE]: boolean;
};

export const getInvoiceStateById = (stateId: number): INVOICE_STATE => {
    switch (stateId) {
        case 0:
            return INVOICE_STATE.UNDEFINED;
        case 10:
            return INVOICE_STATE.NEW;
        case 20:
            return INVOICE_STATE.ASSIGN_WORKFLOW_USERS;
        case 25:
            return INVOICE_STATE.RESTART_AFTER_REJECTED;
        case 30:
            return INVOICE_STATE.READY_FOR_VISA;
        case 41:
            return INVOICE_STATE.VISA_1_OK;
        case 42:
            return INVOICE_STATE.VISA_2_OK;
        case 43:
            return INVOICE_STATE.VISA_3_OK;
        case 49:
            return INVOICE_STATE.FULLY_SIGHTED;
        case 50:
            return INVOICE_STATE.SENT_TO_ERP;
        case 60:
            return INVOICE_STATE.IMPORTED_IN_ERP;
        case 90:
            return INVOICE_STATE.PAID;
        case 95:
            return INVOICE_STATE.REJECTED;
        case 99:
            return INVOICE_STATE.CANCELLED;
        default:
            return INVOICE_STATE.NEW;
    }
};

export class InvoiceListStore {
    rootStore: RootStore;

    invoiceFilter: IInvoiceFilter = {
        state: INVOICE_LIST_STATE.NEW,
        sort: SORT.DATE_NEWEST,
        query: "",
        types: [],
        showOnlyMyInvoices: true,
        showOnlyInvoicesFromMyRealestates: false
    };

    searchTags: ISearchTag[] = [
        {
            label: i18next.t("screens.kredi_flow.list.tags.only_mine"),
            id: "SHOW_ONLY_MY_OWN_INVOICES",
            active: this.invoiceFilter.showOnlyMyInvoices
        },
        {
            label: i18next.t("screens.kredi_flow.list.tags.my_realestates"),
            id: "SHOW_ONLY_INVOICES_FROM_MY_REALESTATES",
            active: false
        },
        { label: i18next.t("screens.kredi_flow.invoice_type.1"), id: "2", active: false, type: INVOICE_TYPE.INVOICE },
        { label: i18next.t("screens.kredi_flow.invoice_type.2"), id: "3", active: false, type: INVOICE_TYPE.BILL }
    ];

    realestateUserData: GetRealestateUserAndAccessData_realestateUsersAndAccess[] = [];

    loadAllInvoices: boolean = false;

    @persist("object")
    batchProcessMap: BatchProcessMap = {};

    @persist
    hasBatchProcessMapInitialized: boolean = false;

    @persist("object")
    areAllInvoicesOfStateSelected: ListStateSelectedMap = {
        [INVOICE_LIST_STATE.NEW]: false,
        [INVOICE_LIST_STATE.TO_APPROVE]: false,
        [INVOICE_LIST_STATE.PAY]: false,
        [INVOICE_LIST_STATE.PAID]: false,
        [INVOICE_LIST_STATE.REJECTED]: false,
        [INVOICE_LIST_STATE.CANCELLED]: false,
        [INVOICE_LIST_STATE.ALL]: false,
        [INVOICE_LIST_STATE.ALL_OPEN]: false,
        [INVOICE_LIST_STATE.ADDITIONAL_INVOICES]: false,
        [INVOICE_LIST_STATE.IMPORT_DOCUMENTS]: false
    };

    @persist
    isBatchProcessing: boolean = false;

    @persist
    currentBatchProcessIndex: number = 0;

    isDownloadingInvoiceFiles: boolean = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            invoiceFilter: observable,
            realestateUserData: observable,
            searchTags: observable,
            loadAllInvoices: observable,
            batchProcessMap: observable,
            areAllInvoicesOfStateSelected: observable,
            isBatchProcessing: observable,
            currentBatchProcessIndex: observable,
            hasBatchProcessMapInitialized: observable,
            isDownloadingInvoiceFiles: observable,

            setFilter: action,
            setFilterState: action,
            clearFilter: action,
            setTag: action,
            setRealestateUserData: action,
            setLoadAllInvoices: action,
            setBatchProcessMapEntry: action,
            toggleSelectAllInvoicesOfState: action,
            setIsBatchProcessing: action,
            setNextBatchProcessIndex: action,
            setPreviousBatchProcessIndex: action,
            setBatchProcessMapInitialized: action,
            terminateBatchProcess: action,
            setCurrentBatchProcessIndex: action,
            downloadInvoiceFilesZip: action,
            navigateToNextBatchProcess: action,
            setIsDownloadingInvoiceFiles: action,
            handleBatchProcessCheckboxClick: action,
            deselectAllInvoices: action,
            resetAreAllInvoicesOfStateSelected: action,

            invoiceStats: computed,
            filteredInvoices: computed,
            invoices: computed,
            selectedBatchProcessInvoiceNumbers: computed,
            hasAnySelectedBatchProcessInvoice: computed,
            nextBatchProcessInvoiceNumber: computed,
            previousBatchProcessInvoiceNumber: computed,
            selectedFileIds: computed
        });
    }

    setIsDownloadingInvoiceFiles = (isDownloadingInvoiceFiles: boolean) => {
        this.isDownloadingInvoiceFiles = isDownloadingInvoiceFiles;
    };

    setBatchProcessMapEntry = (invoiceNumber: number, batchProcess: boolean) => {
        this.batchProcessMap[invoiceNumber] = batchProcess;
    };

    getBatchProcessMapEntry = (invoiceNumber: number) => {
        return this.batchProcessMap[invoiceNumber] ?? false;
    };

    convertInvoiceStateToInvoiceListState = (state: INVOICE_STATE): INVOICE_LIST_STATE => {
        const invoiceStateNumber = Number(state);
        let convertedInvoiceListFilterState = INVOICE_LIST_STATE.ALL;

        switch (true) {
            case invoiceStateNumber >= INVOICE_STATE.NEW && invoiceStateNumber <= INVOICE_STATE.RESTART_AFTER_REJECTED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.NEW;
                break;
            case invoiceStateNumber >= INVOICE_STATE.READY_FOR_VISA &&
                invoiceStateNumber <= INVOICE_STATE.FULLY_SIGHTED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.TO_APPROVE;
                break;
            case invoiceStateNumber >= INVOICE_STATE.SENT_TO_ERP && invoiceStateNumber <= INVOICE_STATE.IMPORTED_IN_ERP:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.PAY;
                break;
            case invoiceStateNumber === INVOICE_STATE.PAID:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.PAID;
                break;
            case invoiceStateNumber === INVOICE_STATE.REJECTED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.REJECTED;
                break;
            case invoiceStateNumber === INVOICE_STATE.CANCELLED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.CANCELLED;
                break;
        }

        return convertedInvoiceListFilterState;
    };

    clearFilter = () => {
        this.searchTags = [...this.searchTags].map((t) => {
            return { ...t, active: false };
        });

        this.setFilter({
            state: this.invoiceFilter.state, // when clearing the filter, keep the state (stay in the same tab)
            sort: SORT.DATE_NEWEST,
            query: "",
            types: [],
            showOnlyMyInvoices: false,
            showOnlyInvoicesFromMyRealestates: false
        });
    };

    setTag = (id: string, active: boolean) => {
        const newSearchTags: ISearchTag[] = [...this.searchTags].map((searchTag) => {
            if (searchTag.id === id) {
                return { ...searchTag, active: active };
            }
            return searchTag;
        });

        const activeSearchTags: INVOICE_TYPE[] = [];

        newSearchTags.forEach((tag) => {
            if (tag.active && tag.type) {
                activeSearchTags.push(tag.type);
            }
        });

        this.searchTags = newSearchTags;

        this.setFilter({
            ...this.invoiceFilter,
            types: activeSearchTags,
            showOnlyMyInvoices:
                newSearchTags.find((searchTag) => searchTag.id === "SHOW_ONLY_MY_OWN_INVOICES")?.active ?? false,
            showOnlyInvoicesFromMyRealestates:
                newSearchTags.find((st) => st.id === "SHOW_ONLY_INVOICES_FROM_MY_REALESTATES")?.active ?? false
        });
    };

    // Setters
    setFilter = (newFilter: IInvoiceFilter) => {
        this.invoiceFilter = newFilter;
    };

    setFilterState = (state: INVOICE_LIST_STATE) => {
        this.setFilter({ ...this.invoiceFilter, state });
    };

    setRealestateUserData = (realestateUserData: GetRealestateUserAndAccessData_realestateUsersAndAccess[]) => {
        this.realestateUserData = realestateUserData;
    };

    setLoadAllInvoices = (loadAllInvoices: boolean) => {
        this.loadAllInvoices = loadAllInvoices;
    };

    toggleSelectAllInvoicesOfState = (invoiceListState: INVOICE_LIST_STATE) => {
        const selected = this.areAllInvoicesOfStateSelected[invoiceListState] ?? false;
        const newSelected = !selected;

        if (invoiceListState === INVOICE_LIST_STATE.ALL) {
            this.areAllInvoicesOfStateSelected = {
                ...this.areAllInvoicesOfStateSelected,
                [INVOICE_LIST_STATE.NEW]: newSelected,
                [INVOICE_LIST_STATE.TO_APPROVE]: newSelected,
                [INVOICE_LIST_STATE.PAY]: newSelected,
                [INVOICE_LIST_STATE.PAID]: newSelected,
                [INVOICE_LIST_STATE.REJECTED]: newSelected,
                [INVOICE_LIST_STATE.CANCELLED]: newSelected,
                [INVOICE_LIST_STATE.ALL_OPEN]: newSelected,
                [INVOICE_LIST_STATE.ALL]: newSelected
            };
        } else if (invoiceListState === INVOICE_LIST_STATE.ALL_OPEN) {
            this.areAllInvoicesOfStateSelected = {
                ...this.areAllInvoicesOfStateSelected,
                [INVOICE_LIST_STATE.NEW]: newSelected,
                [INVOICE_LIST_STATE.TO_APPROVE]: newSelected,
                [INVOICE_LIST_STATE.PAY]: newSelected,
                [INVOICE_LIST_STATE.REJECTED]: newSelected,
                [INVOICE_LIST_STATE.ALL_OPEN]: newSelected
            };
        } else {
            this.areAllInvoicesOfStateSelected = {
                ...this.areAllInvoicesOfStateSelected,
                [invoiceListState]: newSelected
            };
        }

        const invoices = this.filteredInvoices.filter((invoice) => {
            if (invoiceListState === INVOICE_LIST_STATE.ALL) {
                return true;
            } else if (invoiceListState === INVOICE_LIST_STATE.ALL_OPEN) {
                return !(invoice.state === INVOICE_STATE.PAID || invoice.state === INVOICE_STATE.CANCELLED);
            } else {
                let convertedInvoiceListFilterState: INVOICE_LIST_STATE | undefined = undefined;

                const invoiceState: INVOICE_STATE | undefined = invoice.state;
                if (invoiceState) {
                    convertedInvoiceListFilterState = this.convertInvoiceStateToInvoiceListState(invoiceState);
                }

                const currentInvoiceListFilterState: INVOICE_LIST_STATE = invoiceListState;

                return convertedInvoiceListFilterState === currentInvoiceListFilterState;
            }
        });

        invoices.forEach((invoice) => {
            this.setBatchProcessMapEntry(invoice.number ?? 0, newSelected);
        });
    };

    deselectAllInvoices = () => {
        this.batchProcessMap = {};
        this.hasBatchProcessMapInitialized = false;
        this.isBatchProcessing = false;
        this.currentBatchProcessIndex = 0;

        // set all values from batchProcessMap to false
        Object.keys(this.batchProcessMap).forEach((key) => {
            this.batchProcessMap[Number(key)] = false;
        });
    };
    resetAreAllInvoicesOfStateSelected = () => {
        const resetState: ListStateSelectedMap = {} as ListStateSelectedMap;

        // Loop through all enum values to ensure all possible states are reset
        Object.values(INVOICE_LIST_STATE).forEach((state) => {
            // Only process numeric enum values (skip reverse mappings)
            if (typeof state === "number") {
                resetState[state] = false;
            }
        });

        this.areAllInvoicesOfStateSelected = resetState;
    };

    setIsBatchProcessing = (isBatchProcessing: boolean) => {
        this.isBatchProcessing = isBatchProcessing;
    };

    setCurrentBatchProcessIndex = (currentBatchProcessIndex: number) => {
        this.currentBatchProcessIndex = currentBatchProcessIndex;
    };

    setNextBatchProcessIndex = () => {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;

        let nextBatchProcessIndex = this.currentBatchProcessIndex + 1;

        if (nextBatchProcessIndex > maxIndex) {
            nextBatchProcessIndex = 0;
        }

        this.currentBatchProcessIndex = nextBatchProcessIndex;
    };

    setPreviousBatchProcessIndex = () => {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;

        let previousBatchProcessIndex = this.currentBatchProcessIndex - 1;

        if (previousBatchProcessIndex < 0) {
            previousBatchProcessIndex = maxIndex;
        }

        this.currentBatchProcessIndex = previousBatchProcessIndex;
    };

    setBatchProcessMapInitialized = (hasBatchProcessMapInitialized: boolean) => {
        this.hasBatchProcessMapInitialized = hasBatchProcessMapInitialized;
    };

    terminateBatchProcess = () => {
        this.isBatchProcessing = false;

        // set all values from batchProcessMap to false
        Object.keys(this.batchProcessMap).forEach((key) => {
            this.batchProcessMap[Number(key)] = false;
        });

        this.currentBatchProcessIndex = 0;
        this.hasBatchProcessMapInitialized = false;
    };

    downloadInvoiceFiles = async () => {
        this.setIsDownloadingInvoiceFiles(true);

        const selectedFileIds: string[] = this.selectedFileIds;

        if (selectedFileIds.length === 1) {
            const fileIdToDownload = selectedFileIds[0];
            let fileName = undefined;
            const invoice = this.invoices.find((i) => i.invoiceFile?.fileId === fileIdToDownload);

            // invoice date in yyyyMMdd format -> 15.05.2025 = 20250515
            const invoiceDate = invoice?.date ? format(new Date(invoice.date), "yyyyMMdd") : "";
            if (invoice) {
                fileName = `${invoice.number ?? ""}-${invoice.creditor?.name ?? ""}-${invoiceDate}-${
                    invoice.invoiceNumber ?? ""
                }.pdf`;
            }

            await this.rootStore.fileStore.downloadFileAndTriggerDownload(fileIdToDownload, fileName);
        } else if (selectedFileIds.length > 1) {
            await this.downloadInvoiceFilesZip();
        }

        this.setIsDownloadingInvoiceFiles(false);
    };

    downloadInvoiceFilesZip = async (): Promise<any | undefined> => {
        let getInvoiceFilesZipBlob = undefined;

        try {
            const role = this.rootStore.authStore.user?.role;

            const requestBody = {
                files: this.selectedFileIds
            };

            const getInvoiceFilesResponse = await Axios.getAuthInstance().post(
                NetworkConfig.getInvoiceFilesUrl,
                requestBody,
                {
                    responseType: "blob",
                    headers: {
                        "x-hasura-role": getRoleKey(role)
                    }
                }
            );

            const contentDisposition = getInvoiceFilesResponse.headers["Content-Disposition"];

            let fileName = `${format(new Date(), "yyyyMMdd_HHmm")}_${i18next.t(
                "screens.realestate.documents.title"
            )}.zip`;

            if (contentDisposition) {
                // prettier-ignore
                fileName = contentDisposition?.split("filename=")[1]?.replaceAll("\"", "");
            }

            getInvoiceFilesZipBlob = getInvoiceFilesResponse.data;
            this.rootStore.fileStore.triggerDownload(getInvoiceFilesZipBlob, fileName);
        } catch (error) {
            console.error("Error downloading invoice files: ", error);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("stores.file_store.error_downloading_file"),
                MessageType.ERROR
            );
        }

        return getInvoiceFilesZipBlob;
    };

    navigateToNextBatchProcess = () => {
        this.setNextBatchProcessIndex();

        const route = selectRoute(Route.invoiceDetails, this.rootStore.authStore.user?.role, {
            invoicenumber: this.selectedBatchProcessInvoiceNumbers[this.currentBatchProcessIndex].toString()
        });

        this.rootStore.navStore.navigateTo(route);
    };

    handleBatchProcessCheckboxClick = (invoiceNumber: number) => {
        const isCurrentlyChecked = this.batchProcessMap[invoiceNumber] ?? false;

        const newChecked = !isCurrentlyChecked; // Get the new state directly from the event

        if (newChecked) {
            this.setBatchProcessMapEntry(invoiceNumber, true);
        } else {
            this.setBatchProcessMapEntry(invoiceNumber, false);
        }
    };

    // Computed values
    get invoices() {
        return this.rootStore.invoiceStore.invoices;
    }

    get invoiceStats() {
        const allInvoices = [...this.filteredInvoices];

        const getNumberOfTicketsByState = (invoiceListState: INVOICE_LIST_STATE) => {
            switch (invoiceListState) {
                case INVOICE_LIST_STATE.NEW:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            invoice.state >= INVOICE_STATE.NEW &&
                            invoice.state <= INVOICE_STATE.RESTART_AFTER_REJECTED
                    ).length;
                case INVOICE_LIST_STATE.TO_APPROVE:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            invoice.state >= INVOICE_STATE.READY_FOR_VISA &&
                            invoice.state <= INVOICE_STATE.FULLY_SIGHTED
                    ).length;
                case INVOICE_LIST_STATE.PAY:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            invoice.state >= INVOICE_STATE.SENT_TO_ERP &&
                            invoice.state <= INVOICE_STATE.IMPORTED_IN_ERP
                    ).length;
                case INVOICE_LIST_STATE.ALL_OPEN:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            (invoice.state < INVOICE_STATE.PAID || invoice.state === INVOICE_STATE.REJECTED)
                    ).length;
                default:
                    return allInvoices.filter(
                        (invoice) => invoice.state !== undefined && Number(invoice.state) === Number(invoiceListState)
                    ).length;
            }
        };

        return {
            new: getNumberOfTicketsByState(INVOICE_LIST_STATE.NEW),
            toApprove: getNumberOfTicketsByState(INVOICE_LIST_STATE.TO_APPROVE),
            pay: getNumberOfTicketsByState(INVOICE_LIST_STATE.PAY),
            paid: getNumberOfTicketsByState(INVOICE_LIST_STATE.PAID),
            rejected: getNumberOfTicketsByState(INVOICE_LIST_STATE.REJECTED),
            cancelled: getNumberOfTicketsByState(INVOICE_LIST_STATE.CANCELLED),
            allOpen: getNumberOfTicketsByState(INVOICE_LIST_STATE.ALL_OPEN),
            total: { total: this.invoices.length, filtered: allInvoices.length }
        };
    }

    get nextBatchProcessInvoiceNumber() {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;
        let nextBatchProcessIndex = this.currentBatchProcessIndex + 1;

        if (nextBatchProcessIndex > maxIndex) {
            nextBatchProcessIndex = 0;
        }

        const nextBatchProcessInvoiceNumber = this.selectedBatchProcessInvoiceNumbers[nextBatchProcessIndex];

        return nextBatchProcessInvoiceNumber;
    }

    get previousBatchProcessInvoiceNumber() {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;

        let previousBatchProcessIndex = this.currentBatchProcessIndex - 1;

        if (previousBatchProcessIndex < 0) {
            previousBatchProcessIndex = maxIndex;
        }

        const previousBatchProcessInvoiceNumber = this.selectedBatchProcessInvoiceNumbers[previousBatchProcessIndex];

        return previousBatchProcessInvoiceNumber;
    }

    get filteredInvoices() {
        const filter = this.invoiceFilter;
        return this.invoices
            .filter((invoice) => {
                if (filter.query === "") {
                    return true;
                }

                const date = invoice.date
                    ? this.rootStore.uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP
                        ? formatDate(invoice.date ?? format(new Date(), "d.M.yy"))
                        : formatDate(invoice.date ?? format(new Date(), "d.M.yy"), true)
                    : "-";

                const dueDate = invoice.dueDate
                    ? this.rootStore.uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP
                        ? formatDate(invoice.dueDate ?? format(new Date(), "d.M.yy"))
                        : formatDate(invoice.dueDate ?? format(new Date(), "d.M.yy"), true)
                    : "-";

                const searchString = [
                    invoice.number ?? "",
                    date,
                    dueDate,
                    invoice.creditor?.name ?? "",
                    invoice.creditor?.number ?? "",
                    invoice.invoiceNumber ?? "",
                    invoice.amount?.toString() ?? "",
                    invoice.realestate?.name ?? "",
                    invoice.realestate?.city ?? "",
                    invoice.realestate?.zip ?? "",
                    invoice.assignedUser?.name1 ?? "",
                    invoice.assignedUser?.name2 ?? ""
                ]
                    .join("")
                    .toLowerCase();

                return searchString.search(filter.query.toLowerCase()) !== -1;
            })

            .filter((invoice) => {
                if (filter.showOnlyMyInvoices) {
                    if (invoice.assignedUser?.id === this.rootStore.authStore.user?.userid || !invoice.assignedUser) {
                        return true;
                    } else {
                        return false;
                    }
                }
                return true;
            })
            .filter((invoice) => {
                if (filter.showOnlyInvoicesFromMyRealestates) {
                    return this.realestateUserData.some((user) => user.realestateid === invoice.realestate?.id);
                }
                return true;
            })
            .filter((invoice) => {
                if (filter.types.length === 0) {
                    return true;
                }

                if (invoice.invoiceType) {
                    return filter.types.includes(invoice.invoiceType);
                }

                return false;
            })

            .sort((lhs, rhs) => {
                if (filter.sort === SORT.DATE_OLDEST) {
                    return (lhs.number ?? 0) - (rhs.number ?? 0);
                } else if (filter.sort === SORT.DATE_NEWEST) {
                    return (rhs.number ?? 0) - (lhs.number ?? 0);
                } else {
                    return 0;
                }
            });
    }

    get selectedBatchProcessInvoiceNumbers() {
        return Object.entries(this.batchProcessMap)
            .filter(([_, value]) => value === true)
            .map(([invoiceNumber]) => Number(invoiceNumber))
            .sort((a, b) => Number(b) - Number(a));
    }

    get hasAnySelectedBatchProcessInvoice() {
        return Object.entries(this.batchProcessMap).some(([_, value]) => value === true);
    }

    // returns the fileids
    get selectedFileIds() {
        const selectedFileIds: string[] = [];

        this.selectedBatchProcessInvoiceNumbers.forEach((invoiceNumber) => {
            const invoice = this.invoices.find((i) => i.number === invoiceNumber);
            if (invoice && invoice.invoiceFile && invoice.invoiceFile.fileId) {
                selectedFileIds.push(invoice.invoiceFile.fileId);
            }
        });
        return selectedFileIds;
    }
}
